import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, Stat } from '../components/DataTable';
import { ErrorLine } from '../components/ErrorLine';
import { LoadingLine } from '../components/LoadingLine';
import { PageBanner } from '../components/PageBanner';
import { Select } from '../components/Select';
import { api, type CostlyGame, type League } from '../lib/api';
import { logo } from '../lib/logo';
import { teamColor } from '../lib/colors';

/**
 * The games most given away by the man in charge of them.
 *
 * The leaderboard answers who manages a game well across a season, which is the right
 * question and the duller one. This answers which afternoons were thrown, which is the
 * version anyone actually argues about on a Monday.
 */
export function CostliestPage() {
  const [games, setGames] = useState<CostlyGame[] | null>(null);
  const [seasons, setSeasons] = useState<number[]>([]);
  // The user's explicit pick. Undefined means "whatever the latest graded season is", and
  // the season the API resolved is kept separately: writing the response back into this
  // would change the effect's dependency, refetch, and blank the table that had just
  // arrived. Every page that copied this pattern did that on first load.
  const [season, setSeason] = useState<number | undefined>(undefined);
  const [resolved, setResolved] = useState<number | null>(null);
  const [league, setLeague] = useState<League>('nfl');
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    setGames(null);
    return api.costliestGames(league, season, 25)
      .then((r) => {
        setGames(r.games);
        setSeasons(r.seasons);
        setResolved(r.season);
      })
      .catch((e: Error) => setError(e.message));
  };
  useEffect(() => { void load(); }, [league, season]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <PageBanner>
        <div>
            <p className="kicker !text-paper/80">Coach grades</p>
            <h1 className="h1 mt-1">The games they gave away.</h1>
            <p className="mt-3 max-w-[54ch] text-[15px] text-paper/90">
              Every fourth down, two-point try, kick and timeout graded against what usually
              works in that exact situation, then added up per game. Nothing here depends on
              how the plays turned out.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <div className="seg seg-invert">
                {([['nfl', 'NFL'], ['cfb', 'College']] as const).map(([id, label]) => (
                  <button key={id} onClick={() => { setLeague(id); setSeason(undefined); }}
                          className={`seg-item ${league === id ? 'seg-item-on' : ''}`}>{label}</button>
                ))}
              </div>
              {seasons.length > 1 && (
                <Select label="Season" tone="paper" value={season ?? resolved ?? ''}
                        onChange={(v) => setSeason(Number(v))}
                        options={[...seasons].reverse().map((y) => [y, `${y} season`] as const)} />
              )}
            </div>
        </div>
      </PageBanner>

      <section className="section">
        <div className="container stack">
          {error && <ErrorLine onRetry={load} />}
          {!error && !games && <LoadingLine active />}
          {games && !games.length && <p className="body-muted">No graded games for that season yet.</p>}

          {games && games.length > 0 && (
            <DataTable
              rows={games}
              rowKey={(r) => `${r.game_id}:${r.coach}`}
              mobile={(r) => (
                <div className="card flex flex-col gap-3 p-4" style={{ boxShadow: `inset 3px 0 0 0 ${teamColor(r.team_color)}` }}>
                  <div className="min-w-0">
                    <Link to={`/coach/${encodeURIComponent(r.coach)}?league=${league}`} className="truncate text-[16px] font-semibold hover:underline">{r.coach}</Link>
                    <p className="meta !text-[11px]">{r.team_name ?? r.team}{r.opponent ? ` vs ${r.opponent}` : ''}{r.week ? `  Week ${r.week}` : ''}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 rounded-sm bg-white/60 px-3 py-2">
                    <Stat label="Given away" value={`${r.wp_cost_pct.toFixed(1)}%`} tone="text-red" />
                    <Stat label="Calls" value={r.decisions} />
                    <Stat label="Wrong" value={r.wrong} tone={r.wrong ? 'text-red' : ''} />
                  </div>
                  {r.worst_decision?.description && (
                    <p className="line-clamp-2 text-[13px] text-muted">{r.worst_decision.description}</p>
                  )}
                </div>
              )}
              columns={[
                {
                  key: 'coach', header: 'Coach', minWidth: 190,
                  render: (r) => (
                    <span className="flex items-center gap-2">
                      {r.team_logo && <img src={logo(r.team_logo, 24)} alt="" className="h-6 w-6 object-contain" loading="lazy" />}
                      <Link to={`/coach/${encodeURIComponent(r.coach)}?league=${league}`} className="font-medium hover:underline">{r.coach}</Link>
                    </span>
                  ),
                },
                { key: 'game', header: 'Game', minWidth: 150, hideSm: true,
                  render: (r) => <span className="text-muted">{r.opponent ? `vs ${r.opponent}` : r.game_id}{r.week ? `, wk ${r.week}` : ''}</span> },
                { key: 'cost', header: 'Given away', align: 'right', key_stat: true,
                  render: (r) => <span className="text-red">{r.wp_cost_pct.toFixed(1)}%</span> },
                { key: 'calls', header: 'Calls', align: 'right', hideSm: true, render: (r) => r.decisions },
                { key: 'wrong', header: 'Wrong', align: 'right',
                  render: (r) => <span className={r.wrong ? 'text-red' : ''}>{r.wrong}</span> },
              ]}
            />
          )}

          <p className="body-muted max-w-[62ch] text-[13px]">
            Win probability given away is the gap between what he chose and the best option
            available, summed across the game. Only game management counts here, since that is
            the head coach's own call; play calling and defence belong to his staff, and public
            data does not name them.
          </p>
        </div>
      </section>
    </>
  );
}
